import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
  Divider,
  IconButton,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import type React from "react";
import DateRangePicker, { type DateOption } from "../../date-range-picker";
import TimeRangePeriodPicker from "../../time-range-period-picker";
import { type ReportAggregationPeriod } from "../../../generated/graphql";
import { StatisticsView } from "./utils";

const styles = {
  header: {
    border: 1,
    borderBottom: 0,
    borderColor: "#E9ECEF",
    backgroundColor: "#f8f9fa",
    p: 0.5,
  },
  title: { fontWeight: "bold" },
  tabs: { height: 0 },
} satisfies Record<string, SxProps<Theme>>;

export const DocScanVolumeByPipelineHeader = ({
  chartRowsPerPage,
  dateOption,
  isExpanded,
  onDateOptionChange,
  onExpandedChange,
  onExport,
  onPageChange,
  onPeriodChange,
  onViewChange,
  page,
  period,
  totalRows,
  view,
}: {
  readonly chartRowsPerPage: number;
  readonly dateOption: DateOption;
  readonly isExpanded: boolean;
  readonly onDateOptionChange: (dateOption: DateOption) => void;
  readonly onExpandedChange: () => void;
  readonly onExport: () => void;
  readonly onPageChange: (page: number) => void;
  readonly onPeriodChange: (period: ReportAggregationPeriod) => void;
  readonly onViewChange: (view: StatisticsView) => void;
  readonly page: number;
  readonly period: ReportAggregationPeriod;
  readonly totalRows: number;
  readonly view: StatisticsView;
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={styles.header}
  >
    <Stack>
      <Typography sx={styles.title}>Document scan volume</Typography>
      <Typography variant="caption">
        (by pipeline — always shows both, ignores the filter above)
      </Typography>
    </Stack>
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={1}
    >
      {view === StatisticsView.TABLE && (
        <IconButton
          size="small"
          aria-label="Export document scan volume table"
          onClick={() => {
            onExport();
          }}
        >
          <FileDownloadIcon />
        </IconButton>
      )}
      {view === StatisticsView.CHART && (
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={totalRows}
          page={page}
          backIconButtonProps={{ style: { padding: 4 } }}
          nextIconButtonProps={{ style: { padding: 4 } }}
          rowsPerPage={chartRowsPerPage}
          onPageChange={(_event, newPage) => {
            onPageChange(newPage);
          }}
        />
      )}
      <TimeRangePeriodPicker period={period} onChange={onPeriodChange} />
      <Divider flexItem orientation="vertical" />
      <DateRangePicker
        excludeAll
        dateOption={dateOption}
        onChange={onDateOptionChange}
      />
      <Divider flexItem orientation="vertical" />
      <Tabs
        sx={styles.tabs}
        value={view}
        onChange={(_event: React.SyntheticEvent, newValue: StatisticsView) => {
          onViewChange(newValue);
        }}
      >
        <Tab value={StatisticsView.CHART} label="Chart" />
        <Tab value={StatisticsView.TABLE} label="Table" />
      </Tabs>
      <Divider flexItem orientation="vertical" />
      <IconButton
        size="small"
        aria-label={
          isExpanded
            ? "Collapse document scan volume"
            : "Expand document scan volume"
        }
        onClick={onExpandedChange}
      >
        {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
      </IconButton>
    </Stack>
  </Stack>
);
